/** Hover / focus card for a bucket or an edge. Plain React, rendered outside Cytoscape. */
import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import type { ConsolidatedBucket, ConsolidatedEdge } from './types';
import { ROLE_LABEL } from './roleStyle';
import { formatByToken, formatTs, shortAddress, formatAmount } from './format';

export type CardItem =
  | { kind: 'bucket'; bucket: ConsolidatedBucket }
  | { kind: 'edge'; edge: ConsolidatedEdge; sourceLabel: string; targetLabel: string };

function CopyButton({ value, label }: { value: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(ev) => {
        ev.stopPropagation();
        try {
          void navigator.clipboard?.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1200);
        } catch { /* clipboard unavailable */ }
      }}
      className="inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
    >
      {done ? <Check className="w-3 h-3" aria-hidden /> : <Copy className="w-3 h-3" aria-hidden />}
    </button>
  );
}

const MAX_MEMBERS = 8;

export function DetailCard({ item, narrative }: { item: CardItem; narrative?: string | null }) {
  return (
    <div
      role="group"
      aria-label="Details"
      data-testid="consol-card"
      className="w-80 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-card p-3 text-xs shadow-overlay"
    >
      {narrative && <p data-testid="consol-card-narrative" className="mb-2 text-[11px] leading-snug text-foreground">{narrative}</p>}
      {item.kind === 'bucket' ? <BucketBody b={item.bucket} /> : <EdgeBody item={item} />}
    </div>
  );
}

function Flags({ flags }: { flags: string[] }) {
  if (!flags.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1" data-testid="consol-card-flags">
      {flags.map((f) => <span key={f} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono">{f}</span>)}
    </div>
  );
}

function BucketBody({ b }: { b: ConsolidatedBucket }) {
  const shown = b.members.slice(0, MAX_MEMBERS);
  const inTok = formatByToken(b.totals.in_by_token);
  const outTok = formatByToken(b.totals.out_by_token);
  return (
    <div data-testid="consol-card-bucket">
      <div className="font-semibold text-sm">{b.label}</div>
      <div className="text-muted-foreground">
        {ROLE_LABEL[b.role] ?? b.role} · {b.member_count} wallet{b.member_count === 1 ? '' : 's'}{b.chains.length ? ` · ${b.chains.join(', ')}` : ''}
      </div>
      <ul className="mt-2 space-y-1" aria-label="Members">
        {shown.map((m) => {
          const addr = m.address || m.canonical_id;
          return (
            <li key={m.entity_id} className="flex items-center gap-1.5" data-testid="consol-card-member">
              <span className="font-mono">{shortAddress(addr) || m.entity_id}</span>
              {addr && <CopyButton value={addr} label="Copy address" />}
              {m.label && <span className="truncate text-muted-foreground">{m.label}</span>}
              {m.chain && <span className="text-[10px] text-muted-foreground">{m.chain}</span>}
              {m.is_seed && <span className="rounded bg-primary/15 px-1 text-[10px] text-primary">seed</span>}
            </li>
          );
        })}
      </ul>
      {b.member_count > shown.length && <div className="mt-1 text-muted-foreground">and {b.member_count - shown.length} more</div>}
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
        <dt className="text-muted-foreground">In</dt><dd data-testid="consol-card-in">{inTok || 'none'}</dd>
        <dt className="text-muted-foreground">Out</dt><dd data-testid="consol-card-out">{outTok || 'none'}</dd>
        <dt className="text-muted-foreground">Approx. USD</dt><dd>in {formatAmount(b.totals.in_usd)} · out {formatAmount(b.totals.out_usd)}</dd>
        <dt className="text-muted-foreground">Transfers</dt><dd>{b.evidence.transfers}</dd>
        <dt className="text-muted-foreground">First</dt><dd>{formatTs(b.evidence.first_ts)}</dd>
        <dt className="text-muted-foreground">Last</dt><dd>{formatTs(b.evidence.last_ts)}</dd>
        <dt className="text-muted-foreground">Seizure</dt>
        <dd data-testid="consol-card-seizure">
          {b.seizure.serveable_targets} serveable · {b.seizure.corroboration_needed} need corroboration
          {b.seizure.nexus_roles.length ? ` · nexus: ${b.seizure.nexus_roles.join(', ')}` : ''}
        </dd>
      </dl>
      <Flags flags={b.flags} />
    </div>
  );
}

function EdgeBody({ item }: { item: Extract<CardItem, { kind: 'edge' }> }) {
  const e = item.edge;
  return (
    <div data-testid="consol-card-edge">
      <div className="font-semibold text-sm">{item.sourceLabel} to {item.targetLabel}</div>
      <div className="text-muted-foreground">{e.kind.replace(/_/g, ' ')} · {e.transfers} transfer{e.transfers === 1 ? '' : 's'}</div>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
        <dt className="text-muted-foreground">Amount</dt><dd data-testid="consol-card-amount">{formatByToken(e.total_by_token) || 'none'}</dd>
        <dt className="text-muted-foreground">Approx. USD</dt><dd>{formatAmount(e.total_usd)}</dd>
        <dt className="text-muted-foreground">First</dt><dd>{formatTs(e.first_ts)}</dd>
        <dt className="text-muted-foreground">Last</dt><dd>{formatTs(e.last_ts)}</dd>
      </dl>
      {e.sample_tx_hashes.length > 0 && (
        <ul className="mt-2 space-y-1" aria-label="Sample transactions">
          {e.sample_tx_hashes.map((h) => (
            <li key={h} className="flex items-center gap-1.5" data-testid="consol-card-tx">
              <span className="font-mono">{shortAddress(h)}</span><CopyButton value={h} label="Copy transaction hash" />
            </li>
          ))}
        </ul>
      )}
      <Flags flags={e.flags} />
    </div>
  );
}
